import React, { useEffect, useState } from "react";
import { FcExpired, FcOk, FcInfo, FcCurrencyExchange } from "react-icons/fc";
import axios from "axios";

const MyAccount = () => {
  const [domainDetails, setDomainDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState(0);
  const currentDomain = window.location.hostname;

  // Renewal plans
  const renewalPlans = [
    {
      id: 1,
      duration_label: "1 Year",
      duration_days: 365,
      price_inr: 7169.20,
      description: "Standard renewal",
      popular: false
    },
    {
      id: 2,
      duration_label: "2 Years",
      duration_days: 730,
      price_inr: 12293.00,
      description: "Save 15%",
      popular: true
    },
    {
      id: 3,
      duration_label: "3 Years",
      duration_days: 1095,
      price_inr: 19467.00,
      description: "Save 20%",
      popular: false
    }
  ];

  useEffect(() => {
    const checkDomainStatus = async () => {
      try {
        const response = await fetch(
          `https://cyberspacedigital.in/apis/Domain_Renew.php?domain=${currentDomain}`
        );

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();

        if (data.error) {
          setError(data.error);
        } else {
          setDomainDetails(data);
          if (data.expiry_date) {
            const expiryDate = new Date(data.expiry_date);
            const today = new Date();
            const timeDiff = expiryDate.getTime() - today.getTime();
            const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
            setDaysUntilExpiry(daysDiff);
          }
        }
      } catch (error) {
        console.error("Error fetching domain status:", error);
        setError("Unable to fetch account details");
      } finally {
        setLoading(false);
      }
    };

    checkDomainStatus();
  }, [currentDomain]);

// In MyAccount component - update the handleRenewRedirect function:
const handleRenewRedirect = () => {
  const encodedDomain = encodeURIComponent(currentDomain);
  window.location.href = `https://cyberspacedigital.in/domain-renewal/?domain=${encodedDomain}`;
};

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Loading account...</p>
        </div>
      </div>
    );
  }

  if (error || !domainDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-6 rounded-xl shadow-sm border max-w-sm">
          <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <FcExpired className="text-xl" />
          </div>
          <h3 className="text-base font-semibold text-gray-800 mb-2">Load Failed</h3>
          <p className="text-gray-600 text-sm mb-4">{error || "No account details"}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { domain, user_name, purchased_date, expiry_date, tier, notes } = domainDetails;

  const isExpired = daysUntilExpiry <= 0;
  const isWarningPeriod = daysUntilExpiry > 0 && daysUntilExpiry <= 30;

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusConfig = () => {
    if (isExpired) {
      return {
        icon: <FcExpired />,
        title: "Account Expired",
        subtitle: "Subscription has expired",
        status: "Expired",
        description: "Renew to continue service",
        color: "red"
      };
    } else if (isWarningPeriod) {
      return {
        icon: <FcExpired />,
        title: "Account Active",
        subtitle: `Expires in ${daysUntilExpiry} days`,
        status: "Expiring Soon",
        description: "Renew to avoid interruption",
        color: "orange"
      };
    }
    return {
      icon: <FcOk />,
      title: "Account Active",
      subtitle: `Active for ${daysUntilExpiry} days`,
      status: "Active",
      description: "Account in good standing",
      color: "green"
    };
  };

  const status = getStatusConfig();

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4">
        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-4">
          {/* Status Header */}
          <div className="bg-white border-b p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gray-50 rounded-lg">
                  {status.icon}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">{status.title}</h2>
                  <p className="text-gray-600 text-sm">{status.subtitle}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                status.color === 'red' ? 'bg-red-100 text-red-600' :
                status.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                'bg-green-100 text-green-600'
              }`}>
                {status.status}
              </span>
            </div>
          </div>

          {/* Account Details */}
          <div className="p-4 space-y-4">
            {/* Status Alert */}
            <div className={`rounded-lg p-3 text-sm ${
              status.color === 'red' ? 'bg-red-50 text-red-700 border border-red-100' :
              status.color === 'orange' ? 'bg-orange-50 text-orange-700 border border-orange-100' :
              'bg-green-50 text-green-700 border border-green-100'
            }`}>
              <div className="flex items-center space-x-2">
                {status.icon}
                <span className="font-medium">{status.description}</span>
              </div>
            </div>

            {/* Account Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-800 text-sm border-b pb-2">Account Info</h3>
                
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span className="text-xs text-gray-600">Domain</span>
                  <span className="font-medium text-gray-800 text-sm">{domain}</span>
                </div>

                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span className="text-xs text-gray-600">User</span>
                  <span className="font-medium text-gray-800 text-sm">{user_name || "N/A"}</span>
                </div>

                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span className="text-xs text-gray-600">Plan</span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded text-xs font-medium">
                    {tier || "N/A"}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-gray-800 text-sm border-b pb-2">Subscription</h3>
                
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span className="text-xs text-gray-600">Purchased</span>
                  <span className="font-medium text-gray-800 text-sm">{formatDate(purchased_date)}</span>
                </div>

                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span className="text-xs text-gray-600">{isExpired ? "Expired" : "Expires"}</span>
                  <span className="font-medium text-gray-800 text-sm">{formatDate(expiry_date)}</span>
                </div>

                {!isExpired && (
                  <div className={`flex justify-between items-center p-2 rounded ${
                    isWarningPeriod ? 'bg-orange-50' : 'bg-green-50'
                  }`}>
                    <span className="text-xs text-gray-600">Days Left</span>
                    <span className={`font-bold text-sm ${
                      isWarningPeriod ? 'text-orange-600' : 'text-green-600'
                    }`}>
                      {daysUntilExpiry}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {notes && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <FcInfo className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-blue-800 font-medium text-xs mb-1">Notes</p>
                    <p className="text-blue-700 text-sm">{notes}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Renewal Section */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-800 text-sm mb-4">Renew Subscription</h3>
              
              {/* Renewal Plans - Now just for information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {renewalPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`border-2 rounded-lg p-4 ${
                      plan.popular ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
                    } border-gray-200 bg-gray-50`}
                  >
                    {plan.popular && (
                      <div className="inline-block bg-blue-600 text-white text-xs px-2 py-1 rounded-full mb-2">
                        Most Popular
                      </div>
                    )}
                    <h4 className="font-bold text-gray-800 text-lg mb-1">{plan.duration_label}</h4>
                    <p className="text-gray-600 text-sm mb-2">{plan.duration_days} days</p>
                    <div className="flex items-baseline mb-2">
                      <span className="text-2xl font-bold text-green-600">₹{plan.price_inr.toLocaleString('en-IN')}</span>
                    </div>
                    {plan.description && (
                      <p className="text-blue-600 text-xs font-medium">{plan.description}</p>
                    )}
                    <div className="mt-2 text-xs text-gray-500">
                      {plan.duration_days === 365 && 'Standard renewal'}
                      {plan.duration_days === 730 && 'Best value - 15% savings'}
                      {plan.duration_days === 1095 && 'Maximum savings - 20% off'}
                    </div>
                  </div>
                ))}
              </div>

              {/* Renew Button */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="text-center mb-3">
                  <p className="font-medium text-gray-800 mb-1">Ready to renew your domain?</p>
                  <p className="text-sm text-gray-600">
                    Click the button below to proceed to our secure renewal portal
                  </p>
                </div>
                <button
                  onClick={handleRenewRedirect}
                  className="w-full flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors text-sm"
                >
                  <FcCurrencyExchange />
                  <span>Renew Domain</span>
                </button>
                <p className="text-center text-xs text-gray-500 mt-2">
                  You will be redirected to our main renewal portal
                </p>
              </div>

              {/* Contact Information */}
              <div className="text-center">
                <p className="text-gray-500 text-xs mb-2">
                  Need help? Contact: support@cyberspacedigital.in
                </p>
                <p className="text-gray-500 text-xs mb-2">
                  +91 8143407758
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="font-semibold text-gray-800 text-sm mb-3">Account Info</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-600">
            <div>
              <p className="font-medium text-gray-700 mb-1">Status Guide</p>
              <ul className="space-y-1">
                <li>• Active: 30+ days</li>
                <li>• Expiring: 1-30 days</li>
                <li>• Expired: 0 days</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-gray-700 mb-1">Support</p>
              <ul className="space-y-1">
                <li>• support@cyberspacedigital.in</li>
                <li>• +91 81434 07758</li>
                <li>• Mon-Fri, 9AM-7PM</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccount;